import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  FaPlay,
  FaCheck,
  FaExclamationTriangle,
  FaCopy,
  FaExpand,
  FaCompress,
} from 'react-icons/fa';
import { toast } from 'react-toastify';

const SQL_KEYWORDS = [
  'SELECT',
  'FROM',
  'WHERE',
  'AND',
  'OR',
  'NOT',
  'IN',
  'LIKE',
  'BETWEEN',
  'JOIN',
  'LEFT',
  'RIGHT',
  'INNER',
  'OUTER',
  'FULL',
  'CROSS',
  'ON',
  'GROUP',
  'BY',
  'ORDER',
  'ASC',
  'DESC',
  'HAVING',
  'LIMIT',
  'OFFSET',
  'AS',
  'DISTINCT',
  'TOP',
  'WITH',
  'UNION',
  'ALL',
  'EXISTS',
  'CASE',
  'WHEN',
  'THEN',
  'ELSE',
  'END',
  'IS',
  'NULL',
  'COUNT',
  'SUM',
  'AVG',
  'MIN',
  'MAX',
  'CAST',
  'CONVERT',
  'COALESCE',
  'ISNULL',
  'OVER',
  'PARTITION',
  'ROW_NUMBER',
  'RANK',
  'DENSE_RANK',
];

const highlightSQL = (code) => {
  if (!code) return '';
  let html = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  html = html.replace(/'([^']*)'/g, '<span style="color:#e5c07b">\'$1\'</span>');
  html = html.replace(/\b(\d+\.?\d*)\b/g, '<span style="color:#d19a66">$1</span>');

  const keywordPattern = new RegExp(`\\b(${SQL_KEYWORDS.join('|')})\\b`, 'gi');
  html = html.replace(
    keywordPattern,
    (match) => `<span style="color:#c678dd;font-weight:500">${match.toUpperCase()}</span>`
  );

  html = html.replace(/--(.*?)$/gm, '<span style="color:#5c6370;font-style:italic">--$1</span>');

  return html;
};

const QueryEditor = ({ query, onChange, onValidate, readOnly = false, onExecute }) => {
  const textareaRef = useRef(null);
  const highlightRef = useRef(null);
  const wrapperRef = useRef(null);
  const [sqlValue, setSqlValue] = useState(query?.query_text || '');
  const [isExpanded, setIsExpanded] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  useEffect(() => {
    setSqlValue(query?.query_text || '');
    setValidationResult(null);
  }, [query?.query_text, query?.query_id]);

  const handleChange = useCallback(
    (e) => {
      const val = e.target.value;
      setSqlValue(val);
      if (onChange) onChange(val);
      setValidationResult(null);
    },
    [onChange]
  );

  const handleScroll = useCallback(() => {
    if (highlightRef.current && textareaRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        const start = e.target.selectionStart;
        const end = e.target.selectionEnd;
        const val = e.target.value;
        const newVal = val.substring(0, start) + '  ' + val.substring(end);
        setSqlValue(newVal);
        if (onChange) onChange(newVal);
        requestAnimationFrame(() => {
          e.target.selectionStart = e.target.selectionEnd = start + 2;
        });
      }
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && onExecute) {
        e.preventDefault();
        onExecute();
      }
    },
    [onChange, onExecute]
  );

  const handleValidate = async () => {
    if (!onValidate || !sqlValue.trim()) return;
    setValidating(true);
    try {
      const result = await onValidate(sqlValue);
      setValidationResult(result);
    } catch (err) {
      setValidationResult({
        isValid: false,
        errors: [err.response?.data?.message || 'Validation failed'],
      });
    } finally {
      setValidating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(sqlValue);
    toast.success('Copied to clipboard');
  };

  const lineCount = (sqlValue || '').split('\n').length;

  return (
    <div
      className={`rounded-lg overflow-hidden border border-gray-700/50 ${isExpanded ? 'fixed inset-4 z-50 flex flex-col' : ''}`}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#21252b] border-b border-gray-700/50">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
            <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
            <span className="w-3 h-3 rounded-full bg-[#28c840]" />
          </div>
          <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wider ml-2">
            SQL
          </span>
          {!readOnly && (
            <span className="text-[11px] text-gray-600">
              {lineCount} line{lineCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!readOnly && onValidate && (
            <button
              onClick={handleValidate}
              disabled={validating || !sqlValue.trim()}
              className="flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-medium text-gray-400 hover:text-gray-200 hover:bg-surface/5 disabled:opacity-40 transition-colors"
            >
              {validating ? (
                <span className="w-3 h-3 border border-gray-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <FaCheck className="text-[10px]" />
              )}
              Validate
            </button>
          )}
          <button
            onClick={handleCopy}
            className="p-1.5 rounded text-gray-500 hover:text-gray-300 hover:bg-surface/5 transition-colors"
            title="Copy SQL"
          >
            <FaCopy className="text-[11px]" />
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 rounded text-gray-500 hover:text-gray-300 hover:bg-surface/5 transition-colors"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <FaCompress className="text-[11px]" />
            ) : (
              <FaExpand className="text-[11px]" />
            )}
          </button>
          {onExecute && (
            <button
              onClick={onExecute}
              className="flex items-center gap-1.5 px-3 py-1 rounded bg-emerald-600/90 hover:bg-emerald-500 text-white text-[11px] font-medium transition-colors ml-1"
              title="Execute (Ctrl+Enter)"
            >
              <FaPlay className="text-[9px]" />
              Run
            </button>
          )}
        </div>
      </div>

      {/* Editor */}
      <div className={`relative bg-[#282c34] ${isExpanded ? 'flex-1' : 'h-64'}`}>
        {/* Line numbers */}
        <div className="absolute left-0 top-0 bottom-0 w-10 bg-[#282c34] border-r border-gray-700/30 overflow-hidden pointer-events-none z-10">
          <div className="pt-3 px-1.5 text-right">
            {Array.from({ length: lineCount }, (_, i) => (
              <div key={i} className="text-[11px] leading-5 text-gray-600 select-none font-mono">
                {i + 1}
              </div>
            ))}
          </div>
        </div>

        {/* Highlight layer */}
        <pre
          ref={highlightRef}
          className="absolute inset-0 pl-12 pt-3 pr-3 pb-3 overflow-auto font-mono text-[13px] leading-5 text-[#abb2bf] whitespace-pre-wrap break-words pointer-events-none"
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: highlightSQL(sqlValue) + '\n' }}
        />

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={sqlValue}
          onChange={handleChange}
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
          readOnly={readOnly}
          className="absolute inset-0 pl-12 pt-3 pr-3 pb-3 w-full h-full resize-none bg-transparent text-transparent caret-[#528bff] font-mono text-[13px] leading-5 outline-none selection:bg-[#3e4451]"
          spellCheck={false}
          placeholder={readOnly ? '' : 'Write your SQL query...'}
          style={{ caretColor: '#528bff' }}
        />
      </div>

      {/* Validation result */}
      {validationResult && (
        <div
          className={`px-3 py-2 border-t text-xs flex items-start gap-2 ${
            validationResult.isValid
              ? 'bg-[#1e3a2f] border-emerald-800/50 dark:border-emerald-300/50 text-emerald-400'
              : 'bg-[#3a1e1e] border-red-800/50 dark:border-red-300/50 text-red-400'
          }`}
        >
          {validationResult.isValid ? (
            <FaCheck className="text-[10px] mt-0.5 flex-shrink-0" />
          ) : (
            <FaExclamationTriangle className="text-[10px] mt-0.5 flex-shrink-0" />
          )}
          <div>
            <span>
              {validationResult.isValid
                ? 'Query is valid'
                : validationResult.errors?.[0] || 'Query has errors'}
            </span>
            {validationResult.warnings?.length > 0 && (
              <div className="mt-1 text-amber-400/80">
                {validationResult.warnings.map((w, i) => (
                  <div key={i}>{w}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {isExpanded && (
        <div className="fixed inset-0 bg-black/60 -z-10" onClick={() => setIsExpanded(false)} />
      )}
    </div>
  );
};

export default QueryEditor;
